import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Layers, ImageUp, Image, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import type { Passbook, PassbookTier } from '@/shared/types';
import { publicApiBase } from '@/shared/lib/passbook-utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';
import { ColorPicker } from '@/shared/ui/color-picker';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { updateTierSettings, uploadTierImage } from '../api/use-tier-settings';

type BarcodeFormat = 'QR' | 'CODE128';

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const DEFAULT_COLOR = '#9C764C';

/** Miniatura de um asset do tier (strip/logo) — some sozinha se o arquivo ainda não existe. */
function TierImagePreview({ url, label, wide }: { url: string; label: string; wide?: boolean }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <img
        src={url}
        alt={label}
        onError={() => setBroken(true)}
        className={wide ? 'h-10 w-auto max-w-[10rem] rounded border border-border/50 object-contain' : 'h-10 w-10 rounded border border-border/50 object-contain'}
      />
    </div>
  );
}

function emptyTier(): PassbookTier {
  return { level: '', label: '', threshold: 0, order: 0, color: DEFAULT_COLOR };
}

interface TiersSettingsSectionProps {
  idPassbook: string;
  passbook: Passbook;
}

export function TiersSettingsSection({ idPassbook, passbook }: TiersSettingsSectionProps) {
  const queryClient = useQueryClient();
  const [hasConfig, setHasConfig] = useState(Boolean(passbook.hasConfig));
  const [barcodeFormat, setBarcodeFormat] = useState<BarcodeFormat>(
    passbook.barcodeFormat === 'CODE128' ? 'CODE128' : 'QR',
  );
  const [tiers, setTiers] = useState<PassbookTier[]>(
    () => passbook.tiers?.map((t) => ({ ...t })) ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [uploadingLogoIndex, setUploadingLogoIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Cache-bust do preview de imagem — incrementa a cada upload bem-sucedido
  // pra não mostrar a versão antiga cacheada pelo navegador logo depois de trocar.
  const [imageVersion, setImageVersion] = useState<Record<number, number>>({});

  const toggleExpanded = (index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const updateTier = (index: number, patch: Partial<PassbookTier>) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const addTier = () => setTiers((prev) => [...prev, emptyTier()]);
  const removeTier = (index: number) => setTiers((prev) => prev.filter((_, i) => i !== index));

  const handleLabelChange = (index: number, label: string) => {
    const current = tiers[index];
    // Auto-preenche o slug a partir do label só enquanto o nível ainda não
    // teve o level editado manualmente (evita sobrescrever um slug já ajustado).
    const autoLevel = !current.level || current.level === slugify(current.label);
    updateTier(index, { label, ...(autoLevel ? { level: slugify(label) } : {}) });
  };

  const isValid = tiers.every((t) => t.level.trim() && t.label.trim() && t.threshold >= 0);

  const handleImageChange = async (
    index: number,
    file: File | undefined,
    kind: 'strip' | 'logo' = 'strip',
  ) => {
    if (!file) return;
    const tier = tiers[index];
    if (!tier.level.trim()) {
      toast.error('Defina o slug do nível antes de enviar a imagem.');
      return;
    }
    if (file.type !== 'image/png') {
      toast.error('A imagem do cartão precisa ser PNG.');
      return;
    }
    const setUploading = kind === 'logo' ? setUploadingLogoIndex : setUploadingIndex;
    setUploading(index);
    try {
      await uploadTierImage(idPassbook, tier.level, file, kind);
      const label = kind === 'logo' ? 'Logo' : 'Imagem';
      toast.success(`${label} do nível ${tier.label || tier.level} enviada.`);
      setImageVersion((prev) => ({ ...prev, [index]: (prev[index] ?? 0) + 1 }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível enviar a imagem.');
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error('Cada nível precisa de slug, nome e pontuação válidos.');
      return;
    }
    setSaving(true);
    try {
      const ordered = tiers
        .slice()
        .sort((a, b) => a.threshold - b.threshold)
        .map((t, i) => ({ ...t, order: i + 1 }));
      await updateTierSettings(idPassbook, { tiers: ordered, hasConfig, barcodeFormat });
      await queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] });
      setTiers(ordered);
      toast.success('Tiers salvos.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar os tiers.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="size-4" /> Níveis de fidelidade (tiers)
          </CardTitle>
          <Badge variant={passbook.hasTiers ? 'success' : 'secondary'}>
            {passbook.hasTiers ? 'Programa ativo' : 'Programa desligado'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          O nível do cliente é recalculado a cada consulta a partir do saldo de pontos — não é
          preciso reprocessar clientes existentes ao mudar uma faixa aqui.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border/50 p-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="has-config" className="text-sm text-muted-foreground">
              Hub de Configurações liberado p/ loja
            </Label>
            <Switch id="has-config" checked={hasConfig} onCheckedChange={setHasConfig} />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Código de barras do cartão</span>
            <SegmentedControl
              value={barcodeFormat}
              onChange={setBarcodeFormat}
              size="sm"
              options={[
                { value: 'QR', label: 'QR' },
                { value: 'CODE128', label: 'Code128' },
              ]}
            />
          </div>
        </div>
        {!hasConfig ? (
          <p className="text-xs text-amber-600">
            Desligando isso, só quem tem role Admin continua vendo este hub — a loja perde acesso a
            esta tela até você ligar de novo.
          </p>
        ) : null}

        <div className="space-y-3">
          {tiers.map((tier, index) => (
            <div key={index} className="rounded-xl border border-border/50 p-3">
              <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[auto_1fr_1fr_140px_auto_auto_auto_auto]">
                <ColorPicker
                  value={tier.color || DEFAULT_COLOR}
                  onChange={(color) => updateTier(index, { color })}
                  size="compact"
                />
                <div>
                  <Label className="text-xs text-muted-foreground">Nome</Label>
                  <Input
                    value={tier.label}
                    onChange={(e) => handleLabelChange(index, e.target.value)}
                    placeholder="Ex: Invité"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Slug (level)</Label>
                  <Input
                    value={tier.level}
                    onChange={(e) => updateTier(index, { level: slugify(e.target.value) })}
                    placeholder="invite"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pontos p/ atingir</Label>
                  <Input
                    type="number"
                    min={0}
                    value={tier.threshold}
                    onChange={(e) => updateTier(index, { threshold: Number(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label
                    htmlFor={`tier-image-${index}`}
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary/45 hover:text-primary"
                    title="Strip — faixa/banner horizontal do cartão (PNG, não é o cartão inteiro)"
                  >
                    {uploadingIndex === index ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <ImageUp className="size-4" />
                    )}
                  </Label>
                  <Input
                    id={`tier-image-${index}`}
                    type="file"
                    accept="image/png"
                    className="sr-only"
                    disabled={uploadingIndex !== null}
                    onChange={(e) => {
                      void handleImageChange(index, e.target.files?.[0], 'strip');
                      e.target.value = '';
                    }}
                  />
                </div>
                <div>
                  <Label
                    htmlFor={`tier-logo-${index}`}
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary/45 hover:text-primary"
                    title="Logo — ícone quadrado do cartão (PNG, opcional — sem envio usa o logo padrão da loja)"
                  >
                    {uploadingLogoIndex === index ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Image className="size-4" />
                    )}
                  </Label>
                  <Input
                    id={`tier-logo-${index}`}
                    type="file"
                    accept="image/png"
                    className="sr-only"
                    disabled={uploadingLogoIndex !== null}
                    onChange={(e) => {
                      void handleImageChange(index, e.target.files?.[0], 'logo');
                      e.target.value = '';
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => toggleExpanded(index)}
                  aria-label={`Conteúdo do verso — ${tier.label || index + 1}`}
                  title="Conteúdo do verso do cartão"
                >
                  {expanded.has(index) ? (
                    <ChevronUp className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeTier(index)}
                  aria-label={`Remover nível ${tier.label || index + 1}`}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>

              {expanded.has(index) ? (
                <div className="mt-3 grid gap-3 border-t border-border/40 pt-3 sm:grid-cols-2">
                  {tier.level.trim() ? (
                    <div className="flex flex-wrap items-start gap-4 sm:col-span-2">
                      <TierImagePreview
                        label="Strip atual"
                        wide
                        url={`${publicApiBase()}/${idPassbook}/strip_${tier.level}.png?v=${imageVersion[index] ?? 0}`}
                      />
                      <TierImagePreview
                        label="Logo atual"
                        url={`${publicApiBase()}/${idPassbook}/logo_${tier.level}.png?v=${imageVersion[index] ?? 0}`}
                      />
                    </div>
                  ) : null}
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">
                      Cores do cartão (Apple/Google Wallet)
                    </Label>
                    <div className="mt-1 flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <ColorPicker
                          value={tier.backgroundColor || passbook.backgroundColor || DEFAULT_COLOR}
                          onChange={(color) => updateTier(index, { backgroundColor: color })}
                          size="compact"
                        />
                        <span className="text-xs text-muted-foreground">Fundo</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ColorPicker
                          value={tier.labelColor || passbook.labelColor || DEFAULT_COLOR}
                          onChange={(color) => updateTier(index, { labelColor: color })}
                          size="compact"
                        />
                        <span className="text-xs text-muted-foreground">Label</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ColorPicker
                          value={tier.foregroundColor || passbook.foregroundColor || DEFAULT_COLOR}
                          onChange={(color) => updateTier(index, { foregroundColor: color })}
                          size="compact"
                        />
                        <span className="text-xs text-muted-foreground">Texto</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Sem preenchimento, usa as cores padrão do cartão do programa. Válido para
                      Apple Wallet nos três campos; no Google Wallet só a cor de fundo (+ logo)
                      muda por nível.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Título (verso do cartão)
                    </Label>
                    <Input
                      value={tier.contentTitle ?? ''}
                      onChange={(e) => updateTier(index, { contentTitle: e.target.value })}
                      placeholder="Ex: Programa Fixe"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Subtítulo</Label>
                    <Input
                      value={tier.contentSubtitle ?? ''}
                      onChange={(e) => updateTier(index, { contentSubtitle: e.target.value })}
                      placeholder={`Ex: Cliente ${tier.label || ''}`}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">Benefícios visíveis</Label>
                    <Textarea
                      value={tier.contentBenefits ?? ''}
                      onChange={(e) => updateTier(index, { contentBenefits: e.target.value })}
                      placeholder={'• Pontos em dobro às terças\n• Acesso antecipado a coleções'}
                      rows={3}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    Preenchido, este conteúdo substitui o verso padrão do cartão só para clientes
                    deste nível. Vazio, o cartão usa o verso padrão do programa.
                  </p>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <Button variant="secondary" size="sm" onClick={addTier}>
            <Plus className="size-4" /> Adicionar nível
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={saving}>
            Salvar tiers
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
